/**
 * Overview — control room (default layout).
 *
 * Numbers first, then the queue. Where a number cannot be computed from the
 * schema as it stands, the card shows —— and the basis line says what is missing.
 * The 83% "AI handled" headline is real or absent; it is never estimated.
 */
import { useEffect, useState } from 'react';
import { c, mono, radius, severityColour } from '../theme/tokens';
import {
  Card,
  CardHead,
  Eyebrow,
  H1,
  LinkButton,
  Mono,
} from '../components/primitives';
import { HourChart, HourKey, Movement, SentimentBars, Sparkline, ThemeTrack, VolumeChart } from '../components/charts';
import { EmptyRange, FirstRun, LoadingPanel, NotReady, NoValue } from '../components/states';
import { ProStrip } from '../components/LockedStrip';
import { api } from '../api/client';
import type { CallRow, DashboardContext, RangeKey, SummaryResult } from '../api/types';

export function Overview({
  ctx,
  summary,
  onOpenCall,
  onGoThemes,
  onGoCalls,
  onOpenSentimentCalls,
  onPickTopic,
  onUpgrade,
  onConnectCrm,
  onChangeRange,
}: {
  ctx: DashboardContext;
  summary: SummaryResult | null;
  onOpenCall: (id: string) => void;
  onGoThemes: () => void;
  onGoCalls: () => void;
  onOpenSentimentCalls: (date: string, sentiment: 'positive' | 'neutral' | 'frustrated') => void;
  onPickTopic: (topic: string) => void;
  onUpgrade: (tier: 'pro' | 'select') => void;
  onConnectCrm: () => void;
  onChangeRange: (next: RangeKey) => void;
}) {
  const stamp = useStamp(ctx);

  if (!ctx.assistant.live) {
    return (
      <Content>
        <HeaderBlock stamp={stamp} title={`${ctx.branch.name} branch`} />
        <FirstRun branchName={ctx.branch.name} />
      </Content>
    );
  }

  // Initial load gets an honest skeleton. Background refreshes must never
  // replace a usable dashboard with a flashing loading screen every 10 seconds.
  if (!summary) {
    return (
      <Content>
        <HeaderBlock stamp={stamp} title={`${ctx.branch.name} branch, live`} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px,1fr))', gap: 16 }}>
          {[0, 1, 2, 3, 4].map(i => (
            <LoadingPanel key={i} label="LOADING" />
          ))}
        </div>
      </Content>
    );
  }

  const queue = summary.queue;
  const themes = summary.themes;
  const callsHandled = summary.metrics.find(m => m.key === 'calls_handled')?.value ?? 0;

  // Base is a message-taking service, not a customer task-management system.
  // It must never turn Retell's historic/no-outcome analytics into an apparent
  // list of jobs for the agency. The server also suppresses the queue; this is
  // the matching customer-facing surface.
  if (!ctx.capabilities.cap_review_queue) {
    return <BaseMessageCentre ctx={ctx} summary={summary} onOpenCalls={onGoCalls} onOpenCall={onOpenCall} onOpenSentimentCalls={onOpenSentimentCalls} onConnectCrm={onConnectCrm} />;
  }
  const maxThemeVolume = Math.max(...themes.rows.map(t => t.volume), 1);
  const rangeLabel = summary.range?.label ?? 'This range';
  const rangeDescription = summary.range?.description ?? 'this range';
  const lastCallAt = ctx.assistant.lastCallAt ? new Date(ctx.assistant.lastCallAt) : null;
  const lastCallText = lastCallAt
    ? new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: ctx.timeZone,
      }).format(lastCallAt)
    : null;

  // Real count for the Pro strip: viewing requests still sitting unconfirmed.
  const viewingsTaken = queue.items.filter(q => q.severity === 'viewing').length || null;

  return (
    <Content>
      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Eyebrow>{stamp}</Eyebrow>
          <H1>{ctx.branch.name} branch, live</H1>
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap', flex: '0 0 auto' }}
          title={summary.aiHandled.basis}
        >
          <Mono size={10.5} tracking={0.1}>
            AI HANDLED
          </Mono>
          {summary.aiHandled.percent == null ? (
            <NoValue size={30} />
          ) : (
            <span style={{ fontSize: 30, lineHeight: 1, color: c.ink }}>{summary.aiHandled.percent}%</span>
          )}
        </div>
      </div>

      {callsHandled === 0 ? (
        <EmptyRange
          rangeLabel={`No calls · ${rangeLabel}`}
          title={`The assistant has not answered a call ${rangeDescription}.`}
          body={
            lastCallText
              ? `This is not a fault — ${ctx.branch.name} simply had no calls in this window. The most recent call the assistant handled was ${lastCallText}. Widen the range to see it.`
              : `The assistant is on duty for ${ctx.branch.name}. As soon as it answers a call, it appears here with the transcript, what the caller wanted and whatever needs a person.`
          }
          actions={
            <>
              <LinkButton onClick={() => onChangeRange('7d')} size={14}>
                Show the last 7 days →
              </LinkButton>
              <LinkButton onClick={() => onChangeRange('30d')} size={14}>
                Last 30 days →
              </LinkButton>
              <LinkButton onClick={onGoCalls} size={14}>
                Open the call record →
              </LinkButton>
            </>
          }
        />
      ) : null}

      {/* Metric row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px,1fr))', gap: 16 }}>
        {summary.metrics.map(m => {
          const missing = m.notReady && m.notReady.length > 0;
          return (
            <Card key={m.key} padding="22px 20px" gap={14} hover={false} style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: mono,
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  color: c.ink3,
                  lineHeight: 1.5,
                  minHeight: 30,
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: 'clamp(24px, 2.4vw, 34px)',
                  lineHeight: 1,
                  letterSpacing: '-0.02em',
                  color: c.ink,
                  whiteSpace: 'nowrap',
                }}
                title={m.basis}
              >
                {m.value == null ? <NoValue /> : `${m.value}${m.unit === 'percent' ? '%' : ''}`}
              </div>
              <Sparkline values={m.spark} />
              <div
                style={{
                  fontSize: 12.5,
                  color: missing ? c.ink3 : m.tone === 'attention' ? c.orange700 : c.success,
                  lineHeight: 1.5,
                }}
              >
                {missing
                  ? m.key === 'missed_calls_prevented'
                    ? 'not configured'
                    : 'awaiting call analysis'
                  : (m.delta ?? '—')}
              </div>
              {missing ? (
                <div style={{ fontFamily: mono, fontSize: 9.5, lineHeight: 1.6, color: c.orange700 }}>
                  {m.notReady!.join(' · ')}
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>

      {/* Row 2 — action queue + call activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24 }}>
        <Card padding={26}>
          <CardHead
            title="Calls to review"
            right={
              <Mono size={10.5} tracking={0.1}>
                {queue.items.length} OPEN
                {queue.oldestMinutes != null ? ` · OLDEST ${formatAge(queue.oldestMinutes)}` : ''}
              </Mono>
            }
          />
          {queue.items.length === 0 ? (
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3, padding: '8px 0 4px 0' }}>
              Nothing is waiting on a person. Every call {rangeDescription} reached an outcome the assistant could close.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {queue.items.slice(0, 6).map(q => (
                <button
                  key={q.id}
                  className="vp-reset vp-row"
                  onClick={() => onOpenCall(q.callId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '13px 0',
                    borderBottom: `1px solid ${c.lineSoft}`,
                    width: '100%',
                  }}
                >
                  <span
                    style={{
                      width: 3,
                      height: 30,
                      flex: '0 0 3px',
                      borderRadius: radius.pill,
                      background: severityColour[q.severity],
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      fontSize: 14.5,
                      color: c.ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {q.text}
                  </span>
                  <Mono size={10.5}>{q.age}</Mono>
                </button>
              ))}
            </div>
          )}
          {!queue.ready ? (
            <Mono size={10} tracking={0.1} color={c.orange700}>
              DERIVED FROM OPEN OUTCOMES · {queue.notReady.join(' · ')} NOT YET IN THE SCHEMA
            </Mono>
          ) : null}
        </Card>

        <Card padding={26} gap={22}>
          <CardHead
            align="flex-start"
            title={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <h3 style={{ margin: 0, fontSize: 19, lineHeight: 1.2, color: c.ink }}>Call activity</h3>
                <span style={{ fontSize: 13, color: c.ink3 }}>
                  {summary.hoursPeak
                    ? `Peak at ${String(summary.hoursPeak.hour).padStart(2, '0')}:00 · ${summary.hoursPeak.total} ${summary.hoursPeak.total === 1 ? 'call' : 'calls'}`
                    : `No calls ${rangeDescription}`}
                </span>
              </div>
            }
            right={<HourKey />}
          />
          <HourChart hours={summary.hours} />
        </Card>
      </div>

      {/* Row 3 — themes + sentiment */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24 }}>
        <Card padding={26}>
          <CardHead
            title="Conversation themes"
            right={
              themes.ready ? (
                <LinkButton onClick={onGoThemes} underline={false}>
                  Drill down →
                </LinkButton>
              ) : undefined
            }
          />
          {!themes.ready ? (
            <NotReady
              compact
              title="Themes need the transcript analysed."
              body="Each call's topics are written when the call_analyzed webhook event is handled. Until then there is nothing honest to group by."
              objects={themes.notReady}
            />
          ) : themes.rows.length === 0 ? (
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
              No topics recorded for {rangeDescription} yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {themes.rows.slice(0, 5).map(t => (
                <button
                  key={t.name}
                  className="vp-reset"
                  onClick={() => onPickTopic(t.name)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}
                >
                  <span style={{ flex: 1, textAlign: 'left', fontSize: 14.5, color: c.ink }}>{t.name}</span>
                  <ThemeTrack pct={(t.volume / maxThemeVolume) * 100} width={120} />
                  <Mono size={12} color={c.ink} style={{ width: 22, textAlign: 'right' }}>
                    {t.volume}
                  </Mono>
                  <Movement movement={t.movement} />
                </button>
              ))}
            </div>
          )}
        </Card>

        {/*
          These bars are a trailing 7-day trend and do NOT follow the selected
          range. That is deliberate — a one-day sentiment bar is noise — but it
          must be stated, or the reader takes it for the current window.
        */}
        <Card padding={26}>
          <CardHead
            title="Caller sentiment"
            right={
              summary.sentiment.summary ? (
                <Mono size={10.5} tracking={0.1} color={summary.sentiment.summary.positive >= 55 ? c.success : c.orange700}>
                  {summary.sentiment.summary.positive >= 55 ? 'MOSTLY POSITIVE' : 'MIXED'}
                </Mono>
              ) : undefined
            }
          />
          <Mono size={9.5} tracking={0.12} color={c.ink3}>
            LAST {summary.sentiment.trailingDays ?? 7} DAYS · {summary.sentiment.trailingAnalysed ?? 0} ANALYSED CALLS · ANALYSED CALLS ONLY
          </Mono>
          {!summary.sentiment.ready ? (
            <NotReady
              compact
              title="Sentiment is not recorded yet."
              body="A sentiment label is written per call when the transcript is analysed. No score is shown until a real one exists."
              objects={summary.sentiment.notReady}
            />
          ) : summary.sentiment.days.length === 0 ? (
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>No analysed calls in the last seven days.</div>
          ) : (
            <>
              <SentimentBars days={summary.sentiment.days} height={110} barHeight={86} gap={12} labelSize={10} />
              <div style={{ fontSize: 14, lineHeight: 1.6, color: c.ink2, textWrap: 'pretty' }}>
                {summary.sentiment.driver ??
                  'What is driving these figures is written by the analysis step, and is not generated in the browser.'}
              </div>
            </>
          )}
        </Card>
      </div>

      <ProStrip viewingsAsMessages={viewingsTaken} onOpen={() => onUpgrade('pro')} />
    </Content>
  );
}

// ── Layout helpers ───────────────────────────────────────────────────────────

/**
 * Recent calls.
 *
 * Shows a fixed short list, NOT the whole period. This panel previously
 * rendered every completed call in the range inside a scroller, so a 30-day
 * view produced hundreds of rows — an archive pretending to be a summary. The
 * Call Record is the archive; this is a glance. The count is stated on the
 * header so it is obvious the list is a subset, with a link to the full record.
 */
const RECENT_CALLS_SHOWN = 6;

function RecentCompletedCalls({ ctx, summary, onOpenCall, onOpenCalls }: { ctx: DashboardContext; summary: SummaryResult; onOpenCall: (id: string) => void; onOpenCalls: () => void }) {
  const [rows, setRows] = useState<CallRow[]>([]);
  // `/calls` is deliberately paged at 50. Never use the page length as a
  // period total: it turns "View all 50" into a lie on a 300-call month, and
  // filtering this page to completed calls makes the lie even smaller.
  const [periodTotal, setPeriodTotal] = useState(0);
  useEffect(() => {
    api.calls(ctx.branch.id, 'All', null, summary.range.key)
      .then(result => {
        setRows(result.rows.filter(row => row.durationSecs != null));
        setPeriodTotal(result.total);
      })
      .catch(() => {
        setRows([]);
        setPeriodTotal(0);
      });
  }, [ctx.branch.id, summary.range.key]);
  const shown = rows.slice(0, RECENT_CALLS_SHOWN);
  const more = Math.max(0, periodTotal - shown.length);
  return <Card padding={0} gap={0} hover={false} style={{ minHeight: 390, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
    <button className="vp-reset vp-row" onClick={onOpenCalls} aria-label="Open the full Call Record" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '22px 24px 18px', borderBottom: `1px solid ${c.line}`, width: '100%', textAlign: 'left' }}>
      <span><Mono size={10} tracking={0.13} color={c.lapis}>LIVE RECORD</Mono><h3 style={{ margin: '5px 0 0', color: c.ink, fontSize: 21, lineHeight: 1.1 }}>Recent calls</h3></span>
      <Mono size={10} tracking={0.08} color={c.indigo}>OPEN CALL RECORD →</Mono>
    </button>
    {shown.length === 0 ? <div style={{ padding: 24, fontSize: 14.5, color: c.ink3 }}>No completed calls in this period.</div> : <>
      <div style={{ padding: '0 24px' }}>
        {shown.map(row => <button key={row.id} className="vp-reset vp-row" onClick={() => onOpenCall(row.id)} style={{ display:'grid',gridTemplateColumns:'minmax(0, 1fr) auto',gap:12,padding:'16px 0',minHeight:58,textAlign:'left',borderBottom:`1px solid ${c.lineSoft}`,width:'100%' }}>
          <span><strong style={{ display:'block',color:c.ink,fontSize:15 }}>{row.name}</strong><span style={{ display:'block',color:c.ink2,fontSize:12.5,marginTop:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{row.summary || row.category}</span></span>
          <Mono size={10} tracking={0.08} color={c.ink3}>{row.time}</Mono>
        </button>)}
      </div>
      <div style={{ marginTop: 'auto', padding: '16px 24px 20px' }}>
        {more > 0
          ? <LinkButton onClick={onOpenCalls} size={13.5}>{`View all ${periodTotal} calls in this period →`}</LinkButton>
          : <Mono size={10} tracking={0.08} color={c.ink3}>{`SHOWING ALL ${periodTotal} ${periodTotal === 1 ? 'CALL' : 'CALLS'} IN THIS PERIOD`}</Mono>}
      </div>
    </>}
  </Card>;
}

/**
 * Call volume across the selected range — the top chart, above sentiment and
 * call activity. Answers "how busy were we and is it going up", which the
 * hour-of-day chart cannot: that one folds every day onto a single 24-hour
 * axis and so hides trend entirely.
 */
function VolumePanel({ summary, onPickDay }: { summary: SummaryResult; onPickDay?: (date: string) => void }) {
  const days = summary.volume ?? [];
  const total = days.reduce((a, d) => a + d.total, 0);
  return <Card padding={0} gap={0} hover={false} style={{ overflow: 'hidden' }}>
    <div style={{ padding: '24px 26px 16px', display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div>
        <Mono size={10} tracking={0.13} color={c.lapis}>CALL VOLUME</Mono>
        <h2 style={{ margin: '7px 0 0', color: c.ink, fontSize: 26, fontWeight: 400, letterSpacing: '-0.025em' }}>
          {total === 0 ? `No calls ${summary.range.description}` : `${total} ${total === 1 ? 'call' : 'calls'} ${summary.range.description}`}
        </h2>
      </div>
      <HourKey />
    </div>
    <div style={{ padding: '8px 26px 26px', minHeight: 232, background: 'linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 100%)' }}>
      {days.length === 0
        ? <div style={{ padding: '60px 0', fontSize: 14.5, color: c.ink3 }}>No calls recorded in this period.</div>
        : <VolumeChart days={days} height={210} onPickDay={onPickDay} />}
    </div>
  </Card>;
}

function BaseMessageCentre({ ctx, summary, onOpenCalls, onOpenCall, onOpenSentimentCalls, onConnectCrm }: { ctx: DashboardContext; summary: SummaryResult; onOpenCalls: () => void; onOpenCall: (id: string) => void; onOpenSentimentCalls: (date: string, sentiment: 'positive' | 'neutral' | 'frustrated') => void; onConnectCrm: () => void }) {
  return <Content>
    <section style={{ position: 'relative', overflow: 'hidden', padding: '34px 34px 32px', borderRadius: 20, color: '#fff', background: 'linear-gradient(118deg, #051f3a 0%, #094074 62%, #3C6997 100%)', boxShadow: '0 18px 42px rgba(5,31,58,0.16)' }}>
      <div style={{ position: 'absolute', width: 360, height: 360, right: -105, top: -185, borderRadius: '50%', background: 'rgba(90,219,255,0.18)' }} />
      <div style={{ position: 'absolute', width: 210, height: 210, right: 145, bottom: -155, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.16)' }} />
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 28, flexWrap: 'wrap' }}>
        <div><Mono size={10.5} tracking={0.14} color="rgba(255,255,255,0.63)">{useStamp(ctx)}</Mono><h1 style={{ margin: '12px 0 8px', fontSize: 'clamp(34px, 5vw, 54px)', lineHeight: .94, letterSpacing: '-0.045em', fontWeight: 400 }}>Action Centre</h1><div style={{ fontSize: 16, color: 'rgba(255,255,255,0.74)' }}>{ctx.branch.name} · Your reception service at a glance</div></div>
        <div style={{ padding: '12px 14px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: radius.pill, background: 'rgba(255,255,255,0.08)' }}><Mono size={10} tracking={0.12} color="#FFFFFF">{summary.range.longLabel.toUpperCase()}</Mono></div>
      </div>
    </section>
    <section className="vp-action-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))', gap: 1, overflow: 'hidden', borderRadius: radius.card, background: c.line, border: `1px solid ${c.line}` }}>
      <ServiceMetric label="CALLS ANSWERED" value={summary.callFlow.answered} note={summary.range.longLabel} tone={c.indigo} />
      <ServiceMetric label="CONVERSATIONS SHARED WITH BRANCH" value={Math.max(0, summary.callFlow.answered - summary.callFlow.unresolved)} note="Full detail available in Call Record" tone={c.lapis} />
      <ServiceMetric label="SHORT CALLS ABANDONED" value={summary.callFlow.unresolved} note="Caller number and time recorded" tone={c.indigo} />
    </section>
    {/* Volume sits ABOVE sentiment and activity: how busy were we, then how
        did those calls feel, then when in the day they landed. */}
    <VolumePanel summary={summary} />
    <section className="vp-action-duo" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(310px, .85fr)', gap: 22 }}><SentimentSpotlight summary={summary} onOpenSentimentCalls={onOpenSentimentCalls} /><RecentCompletedCalls ctx={ctx} summary={summary} onOpenCall={onOpenCall} onOpenCalls={onOpenCalls} /></section>
    <section className="vp-action-duo" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, .52fr)', gap: 22 }}>
      <ActivityPanel summary={summary} />
      <Card padding={26} gap={14} hover={false} style={{ background: c.surface }}><Mono size={10} tracking={0.13} color={c.lapis}>SERVICE STATUS</Mono><div style={{ fontSize: 24, color: c.ink, lineHeight: 1.05 }}>Reception is on duty</div><div style={{ fontSize: 14.5, lineHeight: 1.55, color: c.ink2 }}>Every completed conversation is held in the call record, ready when your team needs the detail.</div><LinkButton onClick={onOpenCalls} size={14}>Open full call record →</LinkButton></Card>
    </section>
    {ctx.tenant.tier === 'pro' ? <Card padding={26} gap={12}><Eyebrow>YOUR PRO ACTIVATION</Eyebrow><div style={{ fontSize: 20, color: c.ink }}>Link your CRM properties</div><div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink2 }}>Let VoiceProp recognise properties and capture better-qualified enquiries. Your Pro entitlement is already active.</div><LinkButton onClick={onConnectCrm} size={14}>Connect CRM →</LinkButton></Card> : null}
  </Content>;
}

function ServiceMetric({ label, value, note, tone }: { label: string; value: number; note: string; tone: string }) {
  return <div style={{ minHeight: 174, padding: '25px 25px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', background: c.surface2 }}><Mono size={10} tracking={0.13} color={c.ink3}>{label}</Mono><div style={{ fontSize: 'clamp(48px, 6vw, 76px)', fontWeight: 400, letterSpacing: '-0.06em', lineHeight: .78, color: tone }}>{value}</div><div style={{ fontSize: 12.5, color: c.ink3 }}>{note}</div></div>;
}

function SentimentSpotlight({ summary, onOpenSentimentCalls }: { summary: SummaryResult; onOpenSentimentCalls: (date: string, sentiment: 'positive' | 'neutral' | 'frustrated') => void }) {
  const s = summary.sentiment.summary;
  const n = summary.sentiment.counts;
  const total = summary.sentiment.trailingAnalysed ?? 0;
  const notScored = Math.max(0, summary.callFlow.answered - total);
  // Percentages on a small sample are technically true and practically
  // misleading — one frustrated call out of three is "33% frustrated". Lead
  // with counts whenever the sample is thin.
  const thinSample = total > 0 && total < 20;
  return <Card padding={0} gap={0} hover={false} style={{ minHeight: 390, overflow: 'hidden', background: c.indigo900 }}>
    <div style={{ padding: '25px 28px 8px', display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start' }}><div><Mono size={10} tracking={0.14} color="rgba(255,255,255,0.58)">CALLER SENTIMENT</Mono><h2 style={{ margin: '8px 0 0', color: '#fff', fontSize: 29, fontWeight: 400, letterSpacing: '-0.03em' }}>{!s ? 'No sentiment signals yet' : thinSample && n ? `${n.positive} of ${total} positive` : `${s.positive}% positive`}</h2></div>{s ? <div style={{ minWidth: 64, height: 64, display: 'grid', placeItems: 'center', borderRadius: '50%', background: `conic-gradient(${c.success} 0 ${s.positive}%, #AAB5C2 ${s.positive}% ${s.positive + s.neutral}%, ${c.danger} ${s.positive + s.neutral}% 100%)` }}><span style={{ width: 49, height: 49, display: 'grid', placeItems: 'center', borderRadius: '50%', background: c.indigo900, color: '#fff', fontSize: 11 }}>{total}</span></div> : null}</div>
    <div style={{ padding: '0 28px 24px', color: 'rgba(255,255,255,0.62)', fontSize: 12.5 }}>{total} analysed {total === 1 ? 'call' : 'calls'} across the last {summary.sentiment.trailingDays ?? 7} days</div>
    {summary.sentiment.days.length > 0 ? <div style={{ padding: '8px 28px 30px', background: 'rgba(255,255,255,0.055)' }}><SentimentBars days={summary.sentiment.days} height={174} barHeight={140} gap={8} maxWidth={84} labelSize={10.5} onSegmentClick={onOpenSentimentCalls} showCounts /><div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}><SentimentKey label="Positive" colour={c.success} value={s?.positive ?? 0} count={n?.positive} /><SentimentKey label="Neutral" colour="#AAB5C2" value={s?.neutral ?? 0} count={n?.neutral} /><SentimentKey label="Frustrated" colour={c.danger} value={s?.frustrated ?? 0} count={n?.frustrated} /></div>{thinSample ? <div style={{ marginTop: 14, color: 'rgba(255,255,255,0.62)', fontSize: 12, lineHeight: 1.5 }}>Percentages move sharply on a small number of calls — the counts are the fairer read at this volume. Bars are sized by how many calls each day had.</div> : null}{notScored > 0 ? <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.68)', fontSize: 12.5, lineHeight: 1.55 }}>* {notScored} brief or incomplete {notScored === 1 ? 'call was' : 'calls were'} logged with the caller number and time, but without enough conversation to score sentiment.</div> : null}</div> : <div style={{ padding: '42px 28px', color: 'rgba(255,255,255,0.62)', fontSize: 15 }}>Sentiment appears here as calls are analysed.</div>}
  </Card>;
}

function SentimentKey({ label, colour, value, count }: { label: string; colour: string; value: number; count?: number }) { return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'rgba(255,255,255,0.73)', fontSize: 12 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: colour }} />{label} {count != null ? `${count} · ${value}%` : `${value}%`}</span>; }

function ActivityPanel({ summary }: { summary: SummaryResult }) {
  return <Card padding={0} gap={0} hover={false} style={{ overflow: 'hidden' }}><div style={{ padding: '24px 26px 16px', display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}><div><Mono size={10} tracking={0.13} color={c.lapis}>CALL ACTIVITY</Mono><h2 style={{ margin: '7px 0 0', color: c.ink, fontSize: 26, fontWeight: 400, letterSpacing: '-0.025em' }}>{summary.hoursPeak ? `Peak call time: ${String(summary.hoursPeak.hour).padStart(2, '0')}:00` : 'No calls in this period'}</h2></div><HourKey /></div><div style={{ padding: '8px 26px 26px', minHeight: 232, background: 'linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 100%)' }}><HourChart hours={summary.hours} height={210} /></div></Card>;
}

function Content({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '44px 40px 64px 40px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
      }}
    >
      {children}
    </div>
  );
}

function HeaderBlock({ stamp, title }: { stamp: string; title: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Eyebrow>{stamp}</Eyebrow>
      <H1>{title}</H1>
    </div>
  );
}

function useStamp(ctx: DashboardContext): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ctx.timeZone,
  })
    .format(new Date())
    .toUpperCase()
    .replace(/,/g, ' ·')
    .concat(` · ${ctx.branch.name.toUpperCase()}`);
}

function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}M`;
  return `${Math.floor(minutes / 60)}H ${String(minutes % 60).padStart(2, '0')}M`;
}
